/**
 * Footstep bus for fx/audio consumers (dust puffs, puddle splashes, step sounds). Character views
 * publish planted feet here; consumers subscribe through the `characterFootsteps` service:
 *
 *   const steps = ctx.services.tryGet('characterFootsteps');
 *   const off = steps?.subscribe((s) => spawnDust(s.x, s.z, s.intensity));
 *
 * Read-only for consumers. The step object passed to listeners is reused — copy what you keep.
 * Purely a view-layer signal: nothing in the simulation depends on it.
 */

export type FootstepSource = 'player' | 'ped' | 'cop' | 'remote';

export interface CharacterFootstep {
  /** World position of the planted foot (m). */
  x: number;
  z: number;
  side: 'left' | 'right';
  /** Ground speed at the plant (m/s). */
  speed: number;
  /** 0 = gentle walk … 1 = flat-out sprint. */
  intensity: number;
  source: FootstepSource;
}

export interface CharacterFootstepService {
  /** Listen for planted feet; returns an unsubscribe function. */
  subscribe(fn: (step: Readonly<CharacterFootstep>) => void): () => void;
  /** Total steps published (handy for tests / `__bh` snapshots). */
  readonly count: number;
}

export interface FootstepBus extends CharacterFootstepService {
  publish(step: Readonly<CharacterFootstep>): void;
}

export function createFootstepBus(): FootstepBus {
  const listeners = new Set<(s: Readonly<CharacterFootstep>) => void>();
  let count = 0;
  return {
    subscribe(fn) {
      listeners.add(fn);
      return () => listeners.delete(fn);
    },
    get count() {
      return count;
    },
    publish(step) {
      count++;
      for (const l of listeners) l(step);
    },
  };
}

declare module '../../core/services' {
  interface ServiceMap {
    /** Planted-foot events from character views (provided by the player view; see footsteps.ts). */
    characterFootsteps: CharacterFootstepService;
  }
}
